<template>
  <LoadingIndicator
    absolute
    v-if="!teiData"
  />
  <TEIDataError
    v-else-if="teiData.error || teiDataError"
    :message="teiData.error || teiDataError"
  />
  <TEILayoutContainer
    v-else
    :asset-id="assetId"
    :show-check-button="showCheckButton"
    :show-checking-button="showCheckingButton"
    :show-reset-button="showResetButton"
    :show-try-again-button="showTryAgainButton"
    :check-button-enabled="checkButtonEnabled"
    :reset-button-enabled="resetButtonEnabled"
    :scoring-bar="{
      attempts: (showFeedback || updatingAttemptItemTry) ? tries : tries + 1,
      maxAttempts: maxTries,
      score: maxScore,
      showAnswerKey: showAnswerKey,
    }"
    :show-scoring-bar="!showFeedback"
    :show-response-bar="showFeedback"
    :response-bar="{
      state: correct ? 'correct' : 'incorrect',
      content: responseBarContent,
    }"
    :is-displayed="isDisplayed"
    @reset-button-clicked="onResetClicked"
    @check-button-clicked="onCheckClicked"
    @try-again-button-clicked="onTryAgainClicked"
  >
    <div
      class="connections-tei"
      :class="`connections-tei--options-size-${optionCardSize}`"
    >
      <TEIInstructionsCard
        position="left"
        do-sticker-position="top-right"
        class="connections-tei__instructions-card"
        :content="promptContent"
        :max-lines="13"
      />
      <div
        ref="optionsContainer"
        class="connections-tei__options"
      >
        <TEIConnectors
          class="connections-tei__connectors"
          :width="connectorsWidth"
          :height="connectorsHeight"
          :connector-lines="connectorLines"
        />
        <div class="connections-tei__options-row">
          <TEIConnectionCard
            ref="rowOneCards"
            v-for="option in rowOneOptions"
            :key="option.id"
            :id="option.id"
            :resource-url="rowOneOptionsHaveImage ? option.resource.url : null"
            :content="rowOneOptionsHaveCaption ? optionCaption(option) : null"
            :accessibility-content="option.accessibilityContent"
            :selected="isRowOneSelected(option.id)"
            :disabled="!canSelect
              || (currentSelectionHasRowOneId && currentSelectionRowOneId !== option.id)
              || (currentSelectionHasRowTwoId && isRowOneSelected(option.id))"
            :size="optionCardSize"
            :state="optionAnswerState(option, 'rowOneId')"
            :connection-point-position="optionCardSize === 'xs' ? 'right' : 'bottom'"
            :connector-lines="connectorLines"
            :row-two-options="rowTwoOptions"
            @selected-changed="onRowOneSelectedChanged"
          />
        </div>
        <div class="connections-tei__options-row">
          <TEIConnectionCard
            ref="rowTwoCards"
            v-for="option in rowTwoOptions"
            :key="option.id"
            :id="option.id"
            :resource-url="rowTwoOptionsHaveImage ? option.resource.url : null"
            :content="rowTwoOptionsHaveCaption ? optionCaption(option) : null"
            :accessibility-content="option.accessibilityContent"
            :selected="isRowTwoSelected(option.id)"
            :disabled="!canSelect
              || (currentSelectionHasRowTwoId && currentSelectionRowTwoId !== option.id)
              || (currentSelectionHasRowOneId && isRowTwoSelected(option.id))"
            :size="optionCardSize"
            :state="optionAnswerState(option, 'rowTwoId')"
            :connection-point-position="optionCardSize === 'xs' ? 'left' : 'top'"
            :connector-lines="connectorLines"
            :row-one-options="rowOneOptions"
            @selected-changed="onRowTwoSelectedChanged"
          />
        </div>
      </div>
    </div>
  </TEILayoutContainer>
</template>

<script>
import {
  first, last, isNil, find, sortBy, map,
} from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import TEILayoutContainer from '@/components/teis/TEILayoutContainer.vue';
import TEIInstructionsCard from '@/components/teis/TEIInstructionsCard.vue';
import TEIConnectionCard from '@/components/teis/TEIConnectionCard.vue';
import TEIConnectors from '@/components/teis/TEIConnectors.vue';
import TEIDataError from '@/components/teis/TEIDataError.vue';
import fullSlideTEI from '@/mixins/fullSlideTEI';
import modals from '@/mixins/modals';
import { extractHtml } from '@/lib/utils';
import {
  TEI_CHECKING_ANSWERS_MODAL_DELAY,
  TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS,
} from '@/lib/constants';
import {
  useAssessmentStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 5;

export default {
  name: 'ConnectionsTEI',
  components: {
    LoadingIndicator,
    TEILayoutContainer,
    TEIInstructionsCard,
    TEIConnectionCard,
    TEIConnectors,
    TEIDataError,
  },
  mixins: [fullSlideTEI, modals],
  data() {
    return {
      connectorsWidth: 0,
      connectorsHeight: 0,
      connectorLines: [],
      selections: [],
      currentSelection: null,
      teiDataError: null,
      checkingAnswersModalId: 'TEICheckingAnswersModal',
      updatingAttemptItemTry: false,
    };
  },
  created() {
    this.selections = this.mapResponseToSelections(this.attemptItemTry?.response || []);
    this.showFeedback = !!this.attemptItemTry?.checked;
  },
  mounted() {
    this.drawConnectors();
    this.validateTeiData();
  },
  watch: {
    teiData() {
      this.drawConnectors();
      this.validateTeiData();
    },
    selections: {
      handler() {
        this.drawConnectors(true);
        this.onWatchSelectionChanged();
      },
      deep: true,
    },
    currentSelection() {
      this.onWatchSelectionChanged();
    },
    answerKeySelections() {
      this.drawConnectors();
    },
    attemptItemTry(newTry) {
      // if we've just cleared answers
      if (this.attemptIsClearing && !newTry.checked && this.isDisplayed) {
        this.submittingAttempt = false;
        this.selections = this.mapResponseToSelections(newTry?.response || []);
        this.currentSelection = null;
        this.showFeedback = !!newTry.checked;

        // set attempt cleared
        this[types.SET_ATTEMPT_CLEARING](false);
      }

      // This watcher is triggered when a response comes back from the api
      if (this.checkAnswersModal) {
        // Make sure the try number doesn't flicker between renders
        this.updatingAttemptItemTry = true;

        this.checkAnswersModal.close(() => {
          this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
          this.submittingAttempt = false;
          this.selections = this.mapResponseToSelections(newTry.response);
          this.showFeedback = !!newTry.checked;
          this.updatingAttemptItemTry = false;
        });
      }
    },
  },
  computed: {
    ...mapState(useUserStore, [
      'userIsStudent',
    ]),
    ...mapState(useAssessmentStore, [
      'attemptIsClearing',
    ]),
    tryFeedback() {
      // check multilang feedback first
      if (
        this.attemptItemTry?.multilanguage_feedback
        && this.teiLang
        && this.attemptItemTry?.multilanguage_feedback[this.teiLang]
      ) {
        return this.attemptItemTry?.multilanguage_feedback[this.teiLang];
      }
      return this.attemptItemTry?.try_feedback;
    },
    maxTries() {
      return this.attempt?.max_tries || 0;
    },
    hasTriesRemaining() {
      return !this.userIsStudent || this.tries < this.maxTries;
    },
    displayedSelections() {
      return this.showAnswerKey ? this.answerKeySelections : this.selections;
    },
    answerKeySelections() {
      return this.showAnswerKey ? this.answerKey : null;
    },
    showAnswerKey() {
      return this.mode === 'EDIT' && !this.userIsStudent && !!this.answerKey.length;
    },
    canSelect() {
      return this.userCanEditAttempt && !this.showAnswerKey
        && !this.showFeedback && !this.submittingAttempt;
    },
    showCheckButton() {
      return !this.showFeedback && !this.submittingAttempt;
    },
    showResetButton() {
      return !this.showFeedback && !this.submittingAttempt;
    },
    showCheckingButton() {
      return this.submittingAttempt;
    },
    showTryAgainButton() {
      return this.showFeedback && this.hasTriesRemaining && !this.correct;
    },
    checkButtonEnabled() {
      return !!this.selections.length;
    },
    resetButtonEnabled() {
      return !!(this.selections.length || this.currentSelection);
    },
    scoring() {
      return this.teiData?.scoringData?.scoring;
    },
    correct() {
      return !!this.attemptItemTry?.correct;
    },
    correctAnswers() {
      return map(this.attemptItemTry?.correct_answers,
        (response) => Object.keys(response).map((key) => ({
          rowOneId: Number(key),
          rowTwoId: Number(first(response[key])),
        })));
    },
    answerKey() {
      const correct = find(this.scoring, (item) => item.correct);
      if (!correct) return [];
      const response = first(correct.responses);
      if (!response) return [];
      return this.mapResponseToSelections(response);
    },
    responseBarContent() {
      if (this.tryFeedback) return this.tryFeedback;
      return this.hasTriesRemaining
        ? this.$t('Don’t worry! You will have another chance at this question.')
        : this.$t('Hm...Not quite. Check with your teacher if you have questions.');
    },
    optionCardSize() {
      const count = this.rowOneOptions.length;
      if ([2, 3].includes(count)) {
        return 'm';
      }
      if (count === 4) {
        return 's';
      }
      return 'xs';
    },
    rowOneOptions() {
      return this.readOptions(first(this.teiData?.responseFields));
    },
    rowTwoOptions() {
      return this.readOptions(last(this.teiData?.responseFields));
    },
    rowOneOptionsHaveImage() {
      return this.optionsHaveImage(this.rowOneOptions);
    },
    rowOneOptionsHaveCaption() {
      return this.optionsHaveCaption(this.rowOneOptions);
    },
    rowTwoOptionsHaveImage() {
      return this.optionsHaveImage(this.rowTwoOptions);
    },
    rowTwoOptionsHaveCaption() {
      return this.optionsHaveCaption(this.rowTwoOptions);
    },
    currentSelectionHasRowOneId() {
      return !isNil(this.currentSelection?.rowOneId);
    },
    currentSelectionHasRowTwoId() {
      return !isNil(this.currentSelection?.rowTwoId);
    },
    currentSelectionRowOneId() {
      return this.currentSelection?.rowOneId;
    },
    currentSelectionRowTwoId() {
      return this.currentSelection?.rowTwoId;
    },
    promptContent() {
      const prompt = this.teiData?.prompt?.lang?.[this.teiLang]?.text;
      return extractHtml(prompt, new Set(TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS));
    },
  },
  methods: {
    ...mapActions(useAssessmentStore, [
      types.SUBMIT_RESPONSE,
      types.UPDATE_UNSUBMITTED_ATTEMPT,
      types.SET_ATTEMPT_CLEARING,
    ]),
    validateTeiData() {
      if (this.teiData) {
        const rowOneLength = this.rowOneOptions.length;
        const rowTwoLength = this.rowTwoOptions.length;
        if (rowOneLength < MIN_OPTIONS || rowOneLength > MAX_OPTIONS
          || rowTwoLength < MIN_OPTIONS || rowTwoLength > MAX_OPTIONS) {
          this.teiDataError = this.$t('Error: TEI must have between %(min)s and %(max)s objects.',
            { min: MIN_OPTIONS, max: MAX_OPTIONS });
          return;
        }

        if (rowOneLength !== rowTwoLength) {
          this.teiDataError = this.$t('Error: Region 1 and Region 2 must have the same amount of objects.');
        }

        const { from, to } = this.teiData.uiLayout?.settings?.allowConnections;
        if (from !== 'single' || to !== 'single') {
          this.teiDataError = this.$t('Error: Allowing multiple connections to or from an object is not supported.');
        }

        if (this.optionCardSize === 'xs' && [...this.rowOneOptions, ...this.rowTwoOptions].find(this.optionHasImage)) {
          this.teiDataError = this.$t('Error: Cards with images are not supported when there are 5 objects.');
        }

        if (this.optionCardSize === 'xs' && (!this.rowOneOptionsHaveCaption || !this.rowTwoOptionsHaveCaption)) {
          this.teiDataError = this.$t('Error: Every object must have text content.');
        }
      }
    },
    optionAnswerState(option, idProp) {
      const selection = find(this.displayedSelections, (sel) => sel[idProp] === option.id);
      if (!selection) return null;
      const isCorrectAnswer = (answers) => (
        answers.find((ans) => ans.rowOneId === selection.rowOneId
          && ans.rowTwoId === selection.rowTwoId) ? 'correct' : 'incorrect'
      );
      if (this.showAnswerKey) {
        return isCorrectAnswer(this.answerKey);
      }
      if (this.showFeedback) {
        // Pick the first set of correct answers that has the most correct.
        const maxCorrect = Math.max(...this.correctAnswers.map((set) => set?.length || 0));
        const answers = this.correctAnswers.find((set) => set?.length === maxCorrect) || [];
        return isCorrectAnswer(answers);
      }
      return null;
    },
    drawConnectors(animate = false) {
      this.$nextTick(() => {
        if (!this.teiData) return;

        const optionsEl = this.$refs?.optionsContainer;
        if (optionsEl) {
          const optionsRect = optionsEl.getBoundingClientRect();
          this.connectorsWidth = optionsRect.width / this.scale;
          this.connectorsHeight = optionsRect.height / this.scale;

          const rowOneCards = this.$refs?.rowOneCards;
          const rowTwoCards = this.$refs?.rowTwoCards;
          if (rowOneCards && rowTwoCards) {
            this.connectorLines = this.displayedSelections
              .map((selection) => {
                const rowOneCard = rowOneCards.find((comp) => comp.id === selection.rowOneId);
                const rowTwoCard = rowTwoCards.find((comp) => comp.id === selection.rowTwoId);
                if (rowOneCard && rowTwoCard) {
                  const rowOneCardRect = rowOneCard.$el.getBoundingClientRect();
                  const rowTwoCardRect = rowTwoCard.$el.getBoundingClientRect();
                  let rowOnePoint;
                  let rowTwoPoint;
                  if (this.optionCardSize === 'xs') {
                    rowOnePoint = {
                      x: (rowOneCardRect.right - optionsRect.left) / this.scale,
                      y: ((rowOneCardRect.top - optionsRect.top)
                        + (rowOneCardRect.height / 2)) / this.scale,
                    };
                    rowTwoPoint = {
                      x: (rowTwoCardRect.left - optionsRect.left) / this.scale,
                      y: ((rowTwoCardRect.top - optionsRect.top)
                        + (rowTwoCardRect.height / 2)) / this.scale,
                    };
                  } else {
                    rowOnePoint = {
                      x: ((rowOneCardRect.left - optionsRect.left)
                        + (rowOneCardRect.width / 2)) / this.scale,
                      y: (rowOneCardRect.bottom - optionsRect.top) / this.scale,
                    };
                    rowTwoPoint = {
                      x: ((rowTwoCardRect.left - optionsRect.left)
                        + (rowTwoCardRect.width / 2)) / this.scale,
                      y: (rowTwoCardRect.top - optionsRect.top) / this.scale,
                    };
                  }
                  return {
                    fromPoint: selection.rowTwoSelectedFirst ? rowTwoPoint : rowOnePoint,
                    toPoint: selection.rowTwoSelectedFirst ? rowOnePoint : rowTwoPoint,
                    ...selection,
                    animated: animate && !this.connectorLines.find(
                      (line) => line.rowOneId === selection.rowOneId
                        && line.rowTwoId === selection.rowTwoId,
                    ),
                  };
                }
                return null;
              })
              .filter((line) => line);
          }
        }
      });
    },
    onResetClicked() {
      this.currentSelection = null;
      this.selections = [];
    },
    onCheckClicked() {
      this.checkAnswersModal = this.openModalDeferred(TEI_CHECKING_ANSWERS_MODAL_DELAY, {
        id: this.checkingAnswersModalId,
        type: this.checkingAnswersModalId,
      });
      this.submittingAttempt = true;
      this[types.SUBMIT_RESPONSE]({
        teiId: this.assetId,
        payload: this.mapSelectionsToResponse(this.selections),
      });
    },
    onTryAgainClicked() {
      this.selections = [];
      this.currentSelection = null;
      this.showFeedback = false;
    },
    onRowOneSelectedChanged(args) {
      const selection = this.selections.find((item) => item.rowOneId === args.id);
      if (selection) {
        this.currentSelection = {
          rowTwoId: selection.rowTwoId,
          rowTwoSelectedFirst: true,
        };
        this.selections = this.selections.filter((sel) => sel !== selection);
      } else if (this.currentSelectionRowOneId === args.id) {
        this.currentSelection = null;
      } else if (this.currentSelectionHasRowTwoId) {
        const newSelection = {
          ...this.currentSelection,
          rowOneId: args.id,
          rowTwoSelectedFirst: true,
        };
        this.selections.push(newSelection);
        this.currentSelection = null;
      } else {
        this.currentSelection = {
          rowOneId: args.id,
        };
      }
      // When the user's answer changes, flag the attempt as dirty
      // so they get prompted before navigating to another page (and
      // losing their unsubmitted attempt)
      this[types.UPDATE_UNSUBMITTED_ATTEMPT](true);
    },
    onRowTwoSelectedChanged(args) {
      const selection = this.selections.find((item) => item.rowTwoId === args.id);
      if (selection) {
        this.currentSelection = {
          rowOneId: selection.rowOneId,
          rowTwoSelectedFirst: false,
        };
        this.selections = this.selections.filter((sel) => sel !== selection);
      } else if (this.currentSelectionRowTwoId === args.id) {
        this.currentSelection = null;
      } else if (this.currentSelectionHasRowOneId) {
        const newSelection = {
          ...this.currentSelection,
          rowTwoId: args.id,
          rowTwoSelectedFirst: false,
        };
        this.selections.push(newSelection);
        this.currentSelection = null;
      } else {
        this.currentSelection = {
          rowTwoId: args.id,
        };
      }
      // When the user's answer changes, flag the attempt as dirty
      // so they get prompted before navigating to another page (and
      // losing their unsubmitted attempt)
      this[types.UPDATE_UNSUBMITTED_ATTEMPT](true);
    },
    isRowOneSelected(rowOneId) {
      return this.currentSelection?.rowOneId === rowOneId
        || this.selections.some((item) => item.rowOneId === rowOneId);
    },
    isRowTwoSelected(rowTwoId) {
      return this.currentSelection?.rowTwoId === rowTwoId
        || this.selections.some((item) => item.rowTwoId === rowTwoId);
    },
    optionHasImage(option) {
      return option?.resource?.type === 'image' && !!option?.resource?.url;
    },
    optionsHaveImage(options) {
      return options?.length < 5 && options.every(this.optionHasImage);
    },
    optionsHaveCaption(options) {
      return options.every((option) => this.optionCaption(option));
    },
    optionCaption(option) {
      return option.text || option.resource?.mediaCaption || option.resource?.title;
    },
    readOptions(field) {
      if (!field) return [];
      return field.elements
        .map((element) => {
          const option = element.lang[this.teiLang];
          if (option.text) option.text = extractHtml(option.text);
          if (option.resource?.mediaCaption) {
            option.resource.mediaCaption = extractHtml(option.resource.mediaCaption);
          }
          return {
            id: element.id,
            ...option,
            accessibilityContent: option.accessibility
              || option.resource?.mediaCaption
              || option.resource?.title,
          };
        });
    },
    mapResponseToSelections(response) {
      return Object.keys(response).map((id) => ({
        rowOneId: Number(id),
        rowTwoId: Number(first(response[id].objects)),
      }));
    },
    mapSelectionsToResponse(selections) {
      return sortBy(selections, 'rowOneId')
        .reduce((response, sel) => ({
          ...response,
          [sel.rowOneId]: {
            objects: [sel.rowTwoId.toString()],
          },
        }), {});
    },
    onWatchSelectionChanged() {
      if (!this.selections?.length && !this.currentSelection) {
        this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
      }
    },
  },
};
</script>

<style lang="stylus">
.connections-tei {
  display: flex;
  height: 100%;
  padding: 20px;
  padding-inline-start: 0;
  width: 100%;

  &__connectors {
    left: 0;
    position: absolute;
    top: 0;
    z-index: 0;
  }

  &__options {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    position: relative;
    z-index: 1;
  }

  &__options-row {
    display: flex;
  }

  &__instructions-card {
    flex: 0 0 auto;
    padding: 22px $nebula-space-3x $nebula-space-2x $nebula-space-3x;
  }

  &--options-size-xs {
    gap: $nebula-space-5x;

    .connections-tei__options {
      align-items: flex-start;
      flex-direction: row;
      gap: 94px;
    }

    .connections-tei__options-row {
      flex-direction: column;
      gap: $nebula-space-1x;
    }
  }

  &--options-size-s {
    gap: $nebula-space-4x;

    .connections-tei__options {
      gap: $nebula-space-10x;
    }

    .connections-tei__options-row {
      gap: $nebula-space-2x;
    }
  }

  &--options-size-m {
    gap: $nebula-space-4x + $nebula-space-half;

    .connections-tei__options {
      gap: $nebula-space-10x + $nebula-space-1x;
      padding-bottom: $nebula-space-4x;
    }

    .connections-tei__options-row {
      gap: $nebula-space-4x + $nebula-space-half;
    }
  }
}
</style>
